import React, { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { BTN2 } from "./psMasters";

// CPT rate card (UI guide §1): POST /config/cpt_codes/upload-rates — multipart, upsert by code per client.
const URL_ = "/api/edi/data-migration/config/cpt_codes/upload-rates";
export async function uploadRateCard(file, cid) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("client_id", cid);
  return (await api.post(URL_, fd)).data;
}
const q = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
export const saveOneRate = (cid, code, description, rate) =>
  uploadRateCard(new File([`Code,Description,Rate\n${q(code)},${q(description)},${rate}\n`], "rate.csv", { type: "text/csv" }), cid);

export const parseRate = (s) => { const t = String(s ?? "").replace(/[$,\s]/g, ""); if (!t) return null; const n = Number(t); return Number.isFinite(n) && n >= 0 ? n : NaN; };
export const rateOf = (r) => { const v = parseRate(r?.rate ?? r?.price); return v == null || Number.isNaN(v) ? null : v; };
export const fmtRate = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const summary = (d) => `${d.inserted ?? 0} added · ${d.updated ?? 0} updated · ${d.rated ?? 0} with a rate · ${d.skipped ?? 0} skipped`;

export function RateCardPanel({ cid, onDone, onClose }) {
  const inp = useRef(null);
  const [busy, setBusy] = useState(false), [err, setErr] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try { const d = await uploadRateCard(file, cid); await onDone(d); }
    catch (e) { setErr(e?.response?.data?.detail ? describeError(e) : "Couldn't upload that file. Check it has Code, Description and Rate columns."); }
    finally { setBusy(false); if (inp.current) inp.current.value = ""; }
  };
  return (
    <div className="rounded-[14px] border border-[#E9E9EC] bg-[#FBFBFD] p-4 mb-3" data-testid="ps-rate-panel">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[var(--ink)]">Upload a rate card</div>
          <div className="text-[12px] text-[var(--gray2)] mt-1 leading-relaxed">A <b>.csv</b>, <b>.xlsx</b> or <b>.xls</b> file with Code, Description and Rate columns (CPT, HCPCS, Price, Charge or Fee work too). Existing codes are updated, new ones are added, and blank or duplicate rows are skipped. Rates become the default charge on claims.</div>
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className="p-1 rounded-[8px] text-[var(--gray3)] hover:text-[var(--ink)]" data-testid="ps-rate-close"><X size={14} /></button>
      </div>
      <label className={`mt-3 flex items-center justify-center gap-2 rounded-[10px] border border-dashed border-black/[.18] bg-white px-4 py-5 text-[12.5px] font-semibold ${busy ? "text-[var(--gray2)]" : "text-[var(--ink)] cursor-pointer hover:bg-black/[.02]"}`} data-testid="ps-rate-dropzone">
        <input ref={inp} type="file" accept=".csv,.xlsx,.xls" className="hidden" disabled={busy} onChange={(e) => pick(e.target.files?.[0])} data-testid="ps-rate-input" />
        {busy ? <><span className="w-3.5 h-3.5 rounded-full border-2 border-[#5856D6] border-t-transparent animate-spin" />Uploading…</> : <><Upload size={14} />Choose a file</>}
      </label>
      {err ? <div className="mt-2 text-[11.5px] font-semibold text-[#B3261E]" data-testid="ps-rate-error">{err}</div> : null}
    </div>
  );
}

export const RateCardButton = ({ onClick }) => <button type="button" className={BTN2} onClick={onClick} data-testid="ps-rate-upload-btn">Upload rate card</button>;
